import { useState, useEffect } from 'react';
import { X, Lock } from 'lucide-react';
import { useEditMode } from './EditModeProvider';
import { isLockedOut, getRemainingLockoutTime, setLockoutUntil, clearLockout } from '../utils/storage';

interface PinModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function PinModal({ isOpen, onClose }: PinModalProps) {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [lockoutTime, setLockoutTime] = useState(0);
  const { login } = useEditMode();

  const ADMIN_PIN = import.meta.env.VITE_ADMIN_PIN || '5002';
  const MAX_ATTEMPTS = 3;
  const LOCKOUT_TIME = 5 * 60 * 1000;

  // Check for existing lockout on mount/open
  useEffect(() => {
    if (isOpen && isLockedOut()) {
      setIsLocked(true);
      setLockoutTime(getRemainingLockoutTime());
      setError('Account locked due to too many failed attempts.');
    }
  }, [isOpen]);

  // Update countdown timer
  useEffect(() => {
    if (!isLocked) return;

    const interval = setInterval(() => {
      const remaining = getRemainingLockoutTime();
      setLockoutTime(remaining);
      
      if (remaining <= 0) {
        setIsLocked(false);
        setAttempts(0);
        setError('');
        clearLockout();
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isLocked]);

  const formatTime = (ms: number): string => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isLocked) {
      setError(`Account locked. Try again in ${formatTime(lockoutTime)}.`);
      return;
    }

    if (pin === ADMIN_PIN) {
      clearLockout();
      setAttempts(0);
      setPin('');
      setError('');
      login();
      onClose();
    } else {
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      
      if (newAttempts >= MAX_ATTEMPTS) {
        const lockoutUntil = Date.now() + LOCKOUT_TIME;
        setLockoutUntil(lockoutUntil);
        setIsLocked(true);
        setLockoutTime(LOCKOUT_TIME);
        setError('Too many failed attempts. Account locked for 5 minutes.');
      } else {
        setError(`Invalid PIN. ${MAX_ATTEMPTS - newAttempts} attempts remaining.`);
      }
      
      setPin('');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="pin-modal-overlay" onClick={onClose}>
      <div className="pin-modal" onClick={(e) => e.stopPropagation()}>
        <button className="pin-modal-close" onClick={onClose}>
          <X size={20} />
        </button>
        
        <div className="pin-modal-header">
          <Lock size={32} className="pin-icon" />
          <h2>Enter PIN</h2>
          <p>Enter the admin PIN to enable edit mode</p>
        </div>
        
        <form onSubmit={handleSubmit} className="pin-form">
          <input
            type="password"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            placeholder="0000"
            maxLength={4}
            disabled={isLocked}
            autoFocus
          />
          
          <button 
            type="submit" 
            disabled={isLocked || pin.length !== 4}
            className="pin-submit-btn"
          >
            {isLocked ? `Locked (${formatTime(lockoutTime)})` : 'Enable Edit Mode'}
          </button>
          
          {error && <div className="pin-error">{error}</div>}
        </form>
      </div>
    </div>
  );
}
